/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import { useTranslations } from "@i18n/react";
import { useCurrentQueueItem, usePlayer } from "@packages/media";
import { useCallback, useEffect, useState } from "react";

import {
  numericPixels,
  numericValues,
  spacing,
} from "../../../../../../global/emotion";
import { Button } from "../../../../../components/_base/_core/Button/Button.emotion";
import {
  FastForwardIcon,
  Plus10Icon,
} from "../../../../../components/_base/icons/Icon/MediaIcons";
import { Transition } from "../../../../../components/blocks/other/Transition/Transition.emotion";
import { Toast } from "../../../../../components/refactor/Toast/Toast.emotion";
import { END_OF_BROWSER_ASYNC_QUEUE } from "../../../../../lib/constants/endOfBrowserAsyncQueue.constants";
import { TransitionRoot } from "../../../../../providers/TransitionRoot/TransitionRoot.emotion";
import type { WithButtonWrapper } from "../../../../../types/withButtonWrapper.types";
import { useMediaClass } from "../../../../utils/media.emotion";
import { FragmentAsChild } from "../../../primitives/FragmentAsChild/FragmentAsChild";
import { MediaButton } from "../MediaButton/MediaButton.emotion";

export type ForwardButtonProps = WithButtonWrapper<{
  canSkipIntro: boolean;
  skipCSS?: Interpolation;
  forwardCSS?: Interpolation;
}>;

const containerStyle: Interpolation = {
  display: "flex",
  placeItems: "center",
  width: numericPixels[40],
};
const containerExpandedStyle: Interpolation = {
  width: numericPixels[80],
};
const disabledStyle: Interpolation = {
  cursor: "not-allowed",
  opacity: numericValues["0.5"],
};
const skipStyle: Interpolation = {
  top: "initial",
  width: spacing.full,
};
const skipButtonStyle: Interpolation = {
  height: "auto",
  maxHeight: numericPixels[80],
  minHeight: numericPixels[40],
  overflow: "hidden",
  paddingBlock: spacing.s,
  width: "max-content",
};
const skipExpandedStyle: Interpolation = {
  maxWidth: "140%",
  whiteSpace: "normal",
};

export const ForwardButton = ({
  canSkipIntro,
  Wrapper = FragmentAsChild,
  forwardCSS = {},
  skipCSS = {},
}: ForwardButtonProps) => {
  const [value, setValue] = useState<"skipIntro" | "forward">(
    canSkipIntro ? "skipIntro" : "forward",
  );
  const [showNotice, setShowNotice] = useState<
    number | "none" | "hour" | "day"
  >("none");
  const t = useTranslations();
  const player = usePlayer();
  const { currentItem, queue } = useCurrentQueueItem();
  const mediaClass = useMediaClass();
  const [triggerNotice, setTriggerNotice] = useState<boolean>(false);

  const notify = (notice: typeof showNotice) => {
    setShowNotice(notice);
    setTimeout(() => setShowNotice("none"), 3000);
  };

  useEffect(() => {
    setValue(canSkipIntro ? "skipIntro" : "forward");
  }, [canSkipIntro]);

  useEffect(() => {
    if (triggerNotice) {
      if (queue.hourlySkipsRemaining > 0) {
        notify(queue.hourlySkipsRemaining);
      } else {
        notify("hour");
      }
      setTriggerNotice(false);
    }
  }, [triggerNotice, queue?.hourlySkipsRemaining]);

  const handleForward = useCallback(
    async (e) => {
      e.stopPropagation();

      try {
        await player?.skipForward();
      } catch (e) {
        if (e.code === 11001) {
          notify("day");
          return;
        } else if (e.code === 11000) {
          notify("hour");
          return;
        }
      }

      if (currentItem?.type === "radio_song") {
        setTimeout(() => setTriggerNotice(true), END_OF_BROWSER_ASYNC_QUEUE);
      }
    },
    [player, queue?.hourlySkipsRemaining],
  );

  const handleSkipIntro = useCallback(
    (e) => {
      e.stopPropagation();
      player?.skipIntro();
    },
    [player],
  );

  return (
    <>
      <TransitionRoot
        value={value}
        defaultValue={canSkipIntro ? "skipIntro" : "forward"}
        kind={"fade"}
        css={[
          containerStyle,
          mediaClass.includes("expanded") || mediaClass.includes("fullscreen")
            ? containerExpandedStyle
            : null,
        ]}
      >
        <Transition value={"skipIntro"} css={skipStyle}>
          <Wrapper asChild>
            <Button
              variant={"lighten"}
              onClick={handleSkipIntro}
              css={[
                skipButtonStyle,
                (mediaClass.includes("expanded") ||
                  mediaClass.includes("fullscreen")) &&
                  skipExpandedStyle,
                skipCSS,
              ]}
              size={"s"}
            >
              {t("audio_player_skip_intro")}
            </Button>
          </Wrapper>
        </Transition>
        <Transition value={"forward"}>
          <Wrapper asChild>
            <MediaButton
              largeIcon={
                currentItem?.selected_audio?.show_music_controls ||
                currentItem?.type === "radio_song" ? (
                  <FastForwardIcon.XL />
                ) : (
                  <Plus10Icon.XL />
                )
              }
              smallIcon={
                currentItem?.selected_audio?.show_music_controls ||
                currentItem?.type === "radio_song" ? (
                  <FastForwardIcon.Small />
                ) : (
                  <Plus10Icon.Medium />
                )
              }
              css={[
                forwardCSS,
                /* this is a style-only change because tapping the disabled
                 * button requires custom code and a toast rather than just
                 * being disabled */
                (queue.hourlySkipsRemaining <= 0 ||
                  queue.dailySkipsRemaining <= 0) &&
                currentItem?.type === "radio_song"
                  ? disabledStyle
                  : null,
              ]}
              onClick={handleForward}
              tabIndex={0}
            />
          </Wrapper>
        </Transition>
      </TransitionRoot>
      {showNotice !== "none" ? (
        <Toast>
          {t(
            showNotice === "day"
              ? "music_radio_used_all_skips_day"
              : showNotice === "hour"
                ? "music_radio_used_all_skips_hour"
                : "music_radio_skips_remaining",
            {
              music_radio_skips_remaining:
                typeof showNotice === "number" ? showNotice : undefined,
            },
          )}
        </Toast>
      ) : null}
    </>
  );
};
